import React, { useState, useEffect } from 'react';
import { getAccessToken } from "../../services/authApi";
import { fieldReader } from "../../services/loanApi";
import { info, error as logAppInsightError } from '../../utils/logger';
import { getOrgConfig } from '../../utils/organizationConfigUtil';

const ExceptionRequestForm = () => {
    const [formData, setFormData] = useState({
        // Page 1 fields
        brokerCompany: '',
        loanNumber: '',
        loName: '',
        borrowerLastName: '',
        loPhone: '',
        propertyAddress: '',
        loEmail: '',
        propertyCityState: '',
        actExec: '',
        propertyZipCode: '',
        loanProgram: '',
        loanAmount: '',
        loanPurpose: '',
        ltv: '',
        occupancy: '',
        cltv: '',
        rateType: '',
        incomeDocType: '',
        interestOnly: '',
        dscrScore: '',
        qualifyingFico: '',
        dti: '',
        propertyType: '',
        residualIncome: '',
        propertyValue: '',
        reservesMonths: '',
        isAppraisalAvailable: '',
        cashOutAmount: '',
        prepaymentTerm: '',
        stepDowns: '',


        // Page 2 fields
        guidelineException: '',
        additionalParameters: '',
        minReservesRequired: '',
        borrowersReservesAmt: '',
        minFico: '',
        borrowersFico: '',
        maxLtv: '',
        currentLtv: '',
        maxDtiMinDscr: '',
        currentDtiDscr: '',
        yearsOfCleanHousing: '',
        otherCompFactors: '',

        // Page 3 fields (internal use)
        creditRiskDecision: '',
        creditRiskDate: '',
        creditRiskSignature: '',
        secondaryDecision: '',
        investor: '',
        llpa: '',
        secondaryDate: '',
        secondarySignature: '',
        additionalParametersInternal: '',
        certificationAgreement: false,
        loanProgram: '', // initial empty value
        loanPurpose: '',
        occupancy: '',
        rateType: '',
        propertyType: '',
        prepaymentTerm: '',
        stepDowns: '',
        incomeDocType: '',
    });
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [submitted, setSubmitted] = useState(false);
    const [loanID, setLoanID] = useState('');
    const [brokerCompany, setBrokerCompany] = useState('');
    const [loName, setLOName] = useState('');


    useEffect(() => {
        const init = async () => {
            if (window.elli?.script?.guest) {
                try {
                    window.elli.script.guest.create();
                    window.elli.script.getObject("loan").then(async function (loanObject) {
                        loanObject.all().then(async function (loan) {

                            info("TPO Header loaded", {
                                loanid: loan.id,
                                loanNumber: loan.loanNumber || "N/A"
                            });
                            setLoanID(loan.id);
                            const tpoAuthObject = await window.elli.script.getObject("auth");
                            const authCode = await tpoAuthObject.createAuthCode();
                            const token = await getAccessToken(authCode);

                            const allFieldIds = Array.from(new Set([
                                "315", //Broker Company
                                "1612", //LO Name
                            ]));

                            const fieldResponse = await fieldReader(token, allFieldIds, loan.id);
                            setBrokerCompany(fieldResponse["315"]);
                            setLOName(fieldResponse["1612"]);
                            setLoading(false);
                        });
                    });
                } catch (err) {
                    setLoading(false);
                    console.error("SidebarMenu initialization error:", err);
                }
            } else {
                console.warn("elli.script.guest is not available.");
                setLoading(false);
            }
        };

        init();
    }, []);


    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
        setSubmitted(true);
    };

    const nextPage = () => setCurrentPage(currentPage + 1);
    const prevPage = () => setCurrentPage(currentPage - 1);
    const orgConfig = getOrgConfig();
    if (submitted) {
        return (
            <div className="max-w-4xl mx-auto p-8 bg-gray-50 rounded-lg shadow-md text-center">
                <h2 className="text-2xl font-bold text-green-600 mb-4">Exception Request Submitted Successfully!</h2>
                <p className="text-gray-700">Your request has been sent for review. You will be notified once a decision is made.</p>
            </div>
        );
    }
    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center mb-6">
                <div className="mr-4 text-white font-bold text-xl px-3 py-2 rounded">
                    <img
                        src={orgConfig.logo}
                        alt={orgConfig.companyName}
                        className="h-12 mr-4"
                    />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Guideline Exception Request</h1>
                    <p className="text-gray-700 mt-1">Consumer Purpose Exceptions will only be considered for the following:</p>
                    <p className="text-gray-700 font-medium">Loan Amount | LTV | FICO | DTI | Reserves | Max Cash-Out | Acreage | Condo</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
                {currentPage === 1 && (
                    <div>

                        {/* Exception Information Section */}
                        <div className="mb-8">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Reason for Exception</h2>
                            <p className="text-gray-700 mb-6">{orgConfig.exceptionReqMsg}</p>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Exception Type</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="guidelineException"
                                        value={formData.guidelineException}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select exception type</option>
                                        <option value="Guideline Exception">Guideline Exception</option>
                                        <option value="Underwriting Exception">Underwriting Exception</option>
                                        <option value="Investor Exception">Investor Exception</option>
                                        <option value="Compliance Exception">Compliance Exception</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2">
                                    <label className="block text-gray-700 font-medium">Exception Reason</label>
                                </div>
                                <div className="md:col-span-3">
                                    <textarea
                                        name="additionalParameters"
                                        value={formData.additionalParameters}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Explain why the borrower can not meet the guideline/What they can meet/Etc"
                                        required
                                    />
                                </div>
                            </div>

                            <h3 className="text-lg font-semibold text-gray-700 mb-4">Comp Factors:</h3>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Min. Reserves Required:</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="minReservesRequired"
                                        value={formData.minReservesRequired}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Min. FICO:</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="minFico"
                                        value={formData.minFico}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Max LTV:</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="maxLtv"
                                        value={formData.maxLtv}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Max DTI/Min DSCR:</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="maxDtiMinDscr"
                                        value={formData.maxDtiMinDscr}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Borrower's Reserves Amt:</label>
                                </div>
                                <div className="md:col-span-3 flex items-center">
                                    <input
                                        type="checkbox"
                                        name="borrowersReservesAmt"
                                        checked={formData.borrowersReservesAmt}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-gray-700">N/A</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Borrower's FICO:</label>
                                </div>
                                <div className="md:col-span-3 flex items-center">
                                    <input
                                        type="checkbox"
                                        name="borrowersFico"
                                        checked={formData.borrowersFico}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-gray-700">N/A</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Current LTV:</label>
                                </div>
                                <div className="md:col-span-3 flex items-center">
                                    <input
                                        type="checkbox"
                                        name="currentLtv"
                                        checked={formData.currentLtv}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-gray-700">N/A</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Current DTI/DSCR:</label>
                                </div>
                                <div className="md:col-span-3 flex items-center">
                                    <input
                                        type="checkbox"
                                        name="currentDtiDscr"
                                        checked={formData.currentDtiDscr}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-gray-700">N/A</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Years of Clean Housing:</label>
                                </div>
                                <div className="md:col-span-3 flex items-center">
                                    <input
                                        type="checkbox"
                                        name="yearsOfCleanHousing"
                                        checked={formData.yearsOfCleanHousing}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-gray-700">N/A</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                                <div className="md:col-span-2">
                                    <label className="block text-gray-700 font-medium">Other Comp Factors:</label>
                                </div>
                                <div className="md:col-span-3">
                                    <textarea
                                        name="otherCompFactors"
                                        value={formData.otherCompFactors}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Broker Information Section */}
                        <div className="mb-8">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Broker Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Broker Company</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="brokerCompany"
                                        value={formData.brokerCompany}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Loan Number</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="loanNumber"
                                        value={formData.loanNumber}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">LO Name</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="loName"
                                        value={formData.loName}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Borrower Last Name</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="borrowerLastName"
                                        value={formData.borrowerLastName}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">LO Phone</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="tel"
                                        name="loPhone"
                                        value={formData.loPhone}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Property Address</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="propertyAddress"
                                        value={formData.propertyAddress}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">LO E-Mail</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="email"
                                        name="loEmail"
                                        value={formData.loEmail}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Property City, State</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="propertyCityState"
                                        value={formData.propertyCityState}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Act Exec.</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="actExec"
                                        value={formData.actExec}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Property Zip Code</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="propertyZipCode"
                                        value={formData.propertyZipCode}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Loan Information Section */}
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">Loan Information</h3>
                        <div className="mb-8">
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Loan Program</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="loanProgram"
                                        value={formData.loanProgram}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="Ally">Ally</option>
                                        <option value="Activator">Activator</option>
                                        <option value="Accelerator">Accelerator</option>
                                        <option value="Ambassador">Ambassador</option>
                                        <option value="ITIN Activator">ITIN Activator</option>
                                        <option value="ITIN Accelerator">ITIN Accelerator</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Loan Amount</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="loanAmount"
                                        value={formData.loanAmount}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Loan Purpose</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="loanPurpose"
                                        value={formData.loanPurpose}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="Purchase">Purchase</option>
                                        <option value="Rate & Term">Rate & Term</option>
                                        <option value="Cash Out">Cash Out</option>
                                        <option value="Delayed Financing">Delayed Financing</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">LTV</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="ltv"
                                        value={formData.ltv}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Occupancy</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="occupancy"
                                        value={formData.occupancy}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="Primary">Primary</option>
                                        <option value="Second Home">Second Home</option>
                                        <option value="Investment">Investment</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">CLTV</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="cltv"
                                        value={formData.cltv}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Rate Type</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="rateType"
                                        value={formData.rateType}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="Fixed">Fixed</option>
                                        <option value="ARM">ARM</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Income Doc Type</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="incomeDocType"
                                        value={formData.incomeDocType}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select income documentation type</option>
                                        <option value="No Ratio">No Ratio</option>
                                        <option value="DSCR">DSCR</option>
                                        <option value="W2">W2</option>
                                        <option value="WVoe">WVoe</option>
                                        <option value="12 Month Business Bank Statement">12 Month Business Bank Statement</option>
                                        <option value="P&L">P&L</option>
                                        <option value="Tax Returns">Tax Returns</option>
                                        <option value="1099">1099</option>
                                        <option value="Asset Utilization">Asset Utilization</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Interest Only</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="interestOnly"
                                        value={formData.interestOnly}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select option</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">DSCR Score</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="dscrScore"
                                        value={formData.dscrScore}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Qualifying FICO</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="qualifyingFico"
                                        value={formData.qualifyingFico}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">DTI</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="dti"
                                        value={formData.dti}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Property Type</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="propertyType"
                                        value={formData.propertyType}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="SFR/PUD">SFR/PUD</option>
                                        <option value="Attached PUD">Attached PUD</option>
                                        <option value="Warrantable Condo">Warrantable Condo</option>
                                        <option value="Non-Warrantable Condo">Non-Warrantable Condo</option>
                                        <option value="Condotel">Condotel</option>
                                        <option value="2-4 Unit">2-4 Unit</option>
                                        <option value="5-8 Unit">5-8 Unit</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Residual Income</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="residualIncome"
                                        value={formData.residualIncome}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Property Value</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="propertyValue"
                                        value={formData.propertyValue}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Reserves (months)</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="reservesMonths"
                                        value={formData.reservesMonths}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Is Appraisal Available?</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="isAppraisalAvailable"
                                        value={formData.isAppraisalAvailable}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Cash Out Amount</label>
                                </div>
                                <div className="md:col-span-3">
                                    <input
                                        type="text"
                                        name="cashOutAmount"
                                        value={formData.cashOutAmount}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Prepayment Term</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="prepaymentTerm"
                                        value={formData.prepaymentTerm}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select prepayment term</option>
                                        <option value="0">0</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                                <div className="md:col-span-2 flex items-center">
                                    <label className="block text-gray-700 font-medium">Step-downs</label>
                                </div>
                                <div className="md:col-span-3">
                                    <select
                                        name="stepDowns"
                                        value={formData.stepDowns}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select option</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Certification Section */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                            <div className="md:col-span-2"></div>
                            <div className="md:col-span-3">
                                <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                                    <p className="text-gray-700 mb-2">
                                        By submitting this exception request, broker certifies that the information provided is accurate and complete,
                                        that no critical information has been withheld and that any changes to the information provided could change
                                        the decision upon re-review. In addition to any required documentation, please provide any additional
                                        documentation to support the exception request. If exception is approved, any change to the parameters
                                        listed on this approval requires the loan be returned to credit risk for review and decision could change.
                                    </p>
                                    <div className="flex items-center mt-4">
                                        <input
                                            type="checkbox"
                                            id="certificationAgreement"
                                            name="certificationAgreement"
                                            checked={formData.certificationAgreement}
                                            onChange={handleChange}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            required
                                        />
                                        <label htmlFor="certificationAgreement" className="ml-2 block text-gray-700 font-medium">
                                            I Agree, Send Request
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Documentation Section */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                            <div className="md:col-span-2">
                                <h3 className="text-lg font-semibold text-gray-700">Required Documentation</h3>
                            </div>
                            <div className="md:col-span-3">
                                <p className="text-gray-700">
                                    Please submit the completed exception request form, complete 1003, credit report, and any additional relevant documentation such as Asset Statements, Income Documentation, Appraisal, Listing, Letter of Explanations, etc to support the exception request and compensating factors.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                            <div className="md:col-span-2">
                                <h3 className="text-lg font-semibold text-gray-700">Additional Information</h3>
                            </div>
                            <div className="md:col-span-3">
                                <p className="text-gray-700 mb-2">If exception is approved, loan may be subject to exception based pricing.</p>
                                <p className="text-gray-700 mb-2">By submitting this exception request, broker certifies that the information provided is accurate and complete, that no critical information has been withheld, and that any changes to the information provided could change the decision upon re-review. In addition to any required documentation, please provide any additional documentation to support the exception request.</p>
                                <p className="text-gray-700">If exception is approved, both pages of this form must accompany loan submission or underwriting review may be delayed. If file is already active, this form must be in the file prior to clear to close. Any change to the parameters listed on this approval requires the loan be returned to credit risk for review and decision could change.</p>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={nextPage}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                disabled={!formData.certificationAgreement}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default ExceptionRequestForm;